import React from 'react';

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { LinkButton } from '../common/input/Button.jsx';
import { forResource } from '../common/container/Container.jsx';
import { Fullscreen as LoadingFull, Indicator as LoadingIndicator } from '../common/container/Loading.jsx';
import { Popup } from '../common/container/Popup.jsx';

const genericErrorMessage = 'There was an error with your request. Please wait a moment and try again.';
function _Invoice(props) {
    const data = props.data || {};

    // Invoice/Offer Items
    const [items, setItems] = React.useState(undefined);
    const getItems = function (id) {
        if (!id) {
            return;
        }
        Artcloud.api.get(
            Artcloud.url.vendor.marketplace.invoices.items.get(id),
            null,
            (responseData) => setItems(responseData)
        );
    }
    const [loading, setLoading] = React.useState(true);
    React.useEffect(() => {
        props.onRegisterRequestCallback((responseData) => {
            if (responseData.Gallery) {
                if (responseData.Payments) {
                    responseData.hasACH = responseData.Gallery.StripePublishableKey && responseData.Payments.HasACH;
                    responseData.hasAffirm = responseData.Payments.HasAffirm;
                    responseData.hasCard = responseData.Gallery.StripePublishableKey && responseData.Payments.HasCard;
                }
            }
            responseData.hasBalance = responseData.Balance > 0;
            responseData.isOffer = responseData.Type && responseData.Type.Value === 1;

            setLoading(false);
            getItems(responseData.PublicId);
        });
    }, []);

    // Reload invoice
    const handleRefresh = function (id) {
        if (id != props.slug) {
            props.onRefresh(id);

            if (window.history.replaceState) {
                window.history.replaceState(null, null, `${location.origin}/invoice/${id}`);
            }
        } else {
            props.onRefresh();
        }
    }

    const [enabled, setEnabled] = React.useState(true);
    const handleError = function (jqXHR) {
        setEnabled(true);

        let message = genericErrorMessage;
        if (jqXHR && jqXHR.responseJSON && jqXHR.responseJSON.Message) {
            message = jqXHR.responseJSON.Message;
        }
        Artcloud.mkt.modals.error(message);
    }

    const [availabilityModal, setAvailabilityModal] = React.useState(false);
    const checkAvailability = function () {
        if (data.IsAvailable) {
            return true;
        }
        setAvailabilityModal(true);
        return false;
    }
    const handleAvailabilityModalClose = function () {
        setAvailabilityModal(false);
    }

    // Accept Offer
    const handleOfferAccept = function () {
        if (!enabled || !checkAvailability()) {
            return;
        }
        setEnabled(false);
        Artcloud.api.post(
            Artcloud.url.vendor.marketplace.invoices.duplicates(data.PublicId),
            { Remote: true },
            function (responseData) {
                setEnabled(true);
                handleRefresh(responseData.PublicId);
                alert('The gallery has been notified and will reach out soon!');
            },
            handleError
        );
    };

    // Stripe Elements
    const [stripeElementsModal, setStripeElementsModal] = React.useState(undefined);
    const handleStripeElementsModalClose = function () {
        setStripeElementsModal(undefined);
    }
    const handleStripeElementsModalOpen = function (type) {
        if (data.isOffer && !checkAvailability()) {
            return;
        }
        setStripeElementsModal({ type, visible: true });
    }
    if (loading) {
        return <LoadingIndicator />
    }
    const totals = data.Totals || {};
    let coverImageStyle;
    if (data.Gallery && data.Gallery.Logo) {
        coverImageStyle = { backgroundImage: `url('${data.Gallery.Logo.MediumUrl}')`, backgroundSize: 'contain' };
    }
    const canPay = data.hasCard || data.hasACH || data.hasAffirm;
    return (
        <React.Fragment>
            <div className="cover__container cover--invoice">
                <div className="container">
                    <div className="cover__img" style={coverImageStyle} />
                    <div className="cover__content">
                        <div className="cover__name cover__name--gallery">
                            <h3>
                                {
                                    data.Gallery &&
                                    <React.Fragment>
                                        {data.Gallery.Company}
                                        <span className="cover__location">{data.Gallery.Location}</span>
                                    </React.Fragment>
                                }
                            </h3>
                        </div>
                    </div>
                </div>
            </div>
            <div id="invoiceForm" className="form__inner">
                <div className="form__inner__content">
                    <div className="container">
                        <div className="invoice__details">
                            {
                                data.isOffer &&
                                <div className="invoice__sub">
                                    <span className="invoice__sub__label">Valid Through:</span>
                                    <span className="invoice__sub__value">{data.ValidThrough}</span>
                                </div>
                            }
                            {
                                !data.isOffer &&
                                <React.Fragment>
                                    <div className="invoice__sub">
                                        <span className="invoice__sub__label">Invoice Date:</span>
                                        <span className="invoice__sub__value">{data.Date}</span>
                                    </div>
                                    <div className="invoice__sub">
                                        <span className="invoice__sub__label">Invoice #:</span>
                                        <span className="invoice__sub__value">{data.Number}</span>
                                    </div>
                                    {
                                        data.Client &&
                                        data.Client.Name &&
                                        <div className="invoice__sub">
                                            <span className="invoice__sub__label">Bill To:</span>
                                            <span className="invoice__sub__value">{data.Client.Name}</span>
                                        </div>
                                    }
                                </React.Fragment>
                            }
                        </div>
                        {
                            !loading &&
                            items == undefined &&
                            <LoadingIndicator />
                        }
                        {
                            items != undefined &&
                            <div className="invoice__list__cont" id="items">
                                <table>
                                    <tbody>
                                        <tr>
                                            <td>Item</td>
                                            <td>Quantity</td>
                                            <td style={{ textAlign: 'right' }}>Price</td>
                                            {
                                                data.HasItemDiscount &&
                                                <td>Discount</td>
                                            }
                                            <td style={{ textAlign: 'right' }}>Subtotal</td>
                                        </tr>
                                        <tr><td style={{ padding: '5px' }} /></tr>
                                        {items.map((d, i) => <InvoiceItem key={i} d={d} hasItemDiscount={data.HasItemDiscount} />)}
                                    </tbody>
                                </table>
                            </div>
                        }
                    </div>
                    <div className="container spacer--bottom">
                        <div className="invoice__totals">
                            <span className="invoice__sub">
                                <span className="invoice__sub__label">Subtotal:</span>
                                <span className="invoice__sub__value">{totals.Subtotal}</span>
                            </span>
                            {
                                data.HasDiscount &&
                                <span className="invoice__sub">
                                    <span className="invoice__sub__label">Discount:</span>
                                    <span className="invoice__sub__value">{totals.Discount}</span>
                                </span>
                            }
                            <span className="invoice__sub">
                                <span className="invoice__sub__label">Tax:</span>
                                <span className="invoice__sub__value">{totals.Tax}</span>
                            </span>
                            <span className="invoice__sub">
                                <span className="invoice__sub__label">Shipping:</span>
                                <span className="invoice__sub__value">{totals.Shipping}</span>
                            </span>
                            <span className="invoice__sub">
                                <span className="invoice__sub__label">Grand Total:</span>
                                <span className="invoice__sub__value">{totals.GrandTotal}</span>
                            </span>
                            {
                                !data.isOffer &&
                                <React.Fragment>
                                    <span className="invoice__sub">
                                        <span className="invoice__sub__label">Paid:</span>
                                        <span className="invoice__sub__value">{totals.NetPaid}</span>
                                    </span>
                                    <span className="invoice__sub">
                                        <span className="invoice__sub__label">Balance:</span>
                                        <span className="invoice__sub__value">{totals.Balance}</span>
                                    </span>
                                </React.Fragment>
                            }
                        </div>
                    </div>
                    <div className="text--center" style={{ paddingBottom: '10px' }}>
                        {
                            data.isOffer &&
                            <React.Fragment>
                                {
                                    canPay ?
                                        <>
                                            {
                                                (data.hasCard || data.hasACH) &&
                                                <span className="text--center spacer--bottom">
                                                    <LinkButton config={{ color: 'dark', size: 'medium', style: 'fill' }} onClick={handleStripeElementsModalOpen}>Accept Offer & Pay Now</LinkButton>
                                                </span>
                                            }
                                            {
                                                data.hasAffirm &&
                                                <span className="text--center spacer--bottom" style={{ marginLeft: '20px' }}>
                                                    <LinkButton config={{ color: 'dark', size: 'medium', style: 'fill' }} name="affirm" onClick={handleStripeElementsModalOpen}>Accept Offer & Pay Later</LinkButton>
                                                </span>
                                            }
                                        </>
                                        :
                                        <React.Fragment>
                                            <div className="text--center spacer--bottom-s">
                                                <LinkButton config={{ color: 'dark', size: 'medium', style: 'fill' }} onClick={handleOfferAccept}>Accept Offer</LinkButton>
                                            </div>
                                            <div className="text--center spacer--bottom">Once you accept, a gallery representative will be in touch to get your payment details.</div>
                                        </React.Fragment>
                                }
                            </React.Fragment>
                        }
                        {
                            !data.isOffer &&
                            <React.Fragment>
                                {
                                    data.hasBalance &&
                                    canPay &&
                                    <>
                                        {
                                            (data.hasCard || data.hasACH) &&
                                            <span className="text--center spacer--bottom">
                                                <LinkButton config={{ color: 'dark', size: 'medium', style: 'fill' }} onClick={handleStripeElementsModalOpen}>Pay Invoice Now</LinkButton>
                                            </span>
                                        }
                                        {
                                            data.hasAffirm &&
                                            <span className="text--center spacer--bottom" style={{ marginLeft: '20px' }}>
                                                <LinkButton config={{ color: 'dark', size: 'medium', style: 'fill' }} name="affirm" onClick={handleStripeElementsModalOpen}>Buy Now, Pay Later</LinkButton>
                                            </span>
                                        }
                                    </>
                                }
                                {
                                    !data.hasBalance &&
                                    <div className="text--center spacer--bottom">
                                        <LinkButton config={{ color: 'dark', size: 'medium', style: 'fill' }} style={{ cursor: 'default', pointerEvents: 'none' }}>Invoice Paid</LinkButton>
                                    </div>
                                }
                            </React.Fragment>
                        }
                        {
                            data.Comments &&
                            <div className="container">
                                <div dangerouslySetInnerHTML={{ __html: data.Comments }} style={{ margin: 'auto', maxWidth: '75%', padding: '20px 0', textAlign: 'left' }} />
                            </div>
                        }
                    </div>
                </div>
            </div>
            {
                availabilityModal == true &&
                <Popup className="form__container form--popup popup--cont" type="popup" onClose={handleAvailabilityModalClose}>
                    <div>Sorry, this item is already on an invoice, please contact the gallery about your offer.</div>
                </Popup>
            }
            {
                stripeElementsModal &&
                stripeElementsModal.visible == true &&
                <StripeElements data={data} type={stripeElementsModal.type} onClose={handleStripeElementsModalClose} onComplete={handleRefresh} />
            }
            {
                !enabled &&
                <LoadingFull style={{ backgroundColor: '#222' }} />
            }
        </React.Fragment>
    );
}

function InvoiceItem(props) {
    const data = props.d || {};
    const item = data.Item || {};
    return (
        <tr className="invoice__list">
            <td>
                <div className="invoice__list__item">
                    <div className="invoice__list__img">
                        {
                            item.Images &&
                            item.Images.length > 0 &&
                            <img src={`${item.Images[0].SmallUrl}`} />
                        }
                    </div>
                    <div className="invoice__list__name">{item.Title}</div>
                    <div className="invoice__list__name invoice__list__details">{item.Artist && item.Artist.Name} | {item.Dimensions && item.Dimensions.Formatted}</div>
                </div>
            </td>
            <td>{data.Quantity}</td>
            <td style={{ textAlign: 'right' }}>{data.Price}</td>
            {
                props.hasItemDiscount &&
                <td>{data.Discount}</td>
            }
            <td style={{ textAlign: 'right' }}>{data.Subtotal}</td>
        </tr>
    );
}

function StripeElements(props) {
    const data = props.data || {};
    const totals = data.Totals || {};
    const type = data.isOffer ? 'offer' : 'invoice';

    const [stripePromise, setStripePromise] = React.useState(undefined);
    React.useEffect(() => {
        if (!data.Gallery) {
            return;
        }
        let options;
        if (data.Gallery && !data.Gallery.IsExpress) {
            options = { stripeAccount: data.Gallery.StripeUserId }
        }
        setStripePromise(loadStripe(data.Gallery.StripePublishableKey, options));
    }, []);

    const isAffirm = props.type == 'affirm'
    const [paymentMethodTypes, setPaymentMethodTypes] = React.useState(undefined)
    React.useEffect(() => {
        const _paymentMethodTypes = []
        if (isAffirm) {
            if (data.hasAffirm) {
                _paymentMethodTypes.push('affirm')
            }
        } else {
            if (data.hasCard) {
                _paymentMethodTypes.push('card');
            }
            if (data.hasACH) {
                _paymentMethodTypes.push('us_bank_account')
            }
        }
        setPaymentMethodTypes(_paymentMethodTypes);
    }, []);

    const [loading, setLoading] = React.useState(true);
    const resetLoading = () => setLoading(false)
    const handleClose = function (event) {
        if (event) {
            event.preventDefault();
        }
        if (!loading && props.onClose) {
            props.onClose();
        }
    }
    const _handleSubmit = (stripe, confirmationToken, publicId, onError) => {
        Artcloud.api.post(
            Artcloud.url.vendor.marketplace.invoices.paymentIntents.post(publicId),
            { ConfirmationTokenId: confirmationToken.id, PaymentMethodTypes: paymentMethodTypes },
            async (response) => {
                if (!response) {
                    return;
                }
                if (response.Status == 'requires_action') {
                    const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret: response.ClientSecret })
                    if (error) {
                        if (onError) {
                            onError(error && error.message);
                        }
                        return;
                    } else if (paymentIntent && paymentIntent.next_action) {
                        Artcloud.api.del(Artcloud.url.vendor.marketplace.invoices.paymentIntents.del(publicId, paymentIntent.id));
                        if (onError) {
                            onError(<span>Could not process the payment using the selected method.<br />Please choose another to proceed.</span>);
                        }
                        return;
                    }
                }
                window.location.href = `${window.location.origin}/invoice/${publicId}/confirmation?payment_intent_client_secret=${response.ClientSecret}&payment_intent=${response.Id}`
            }
        ).fail(jqXHR => {
            if (onError) {
                onError(jqXHR && jqXHR.responseJSON && jqXHR.responseJSON.Message);
            }
        });
    }
    const handleSubmit = async (stripe, elements, callback) => {
        const handleError = (message) => {
            resetLoading();
            if (callback) {
                callback(false, message);
            }
        }
        setLoading(true);
        const { error: submitError } = await elements.submit()
        if (submitError) {
            handleError(submitError.message)
            return;
        }
        const params = {};
        if (data.Client) {
            params.shipping = {
                name: data.Name,
                address: {
                    line1: data.AddressLineOne,
                    line2: data.AddressLineTwo,
                    city: data.City,
                    state: data.State,
                    country: data.Country,
                    postal_code: data.Zip
                }
            }
        }
        const { error: confirmationError, confirmationToken } = await stripe.createConfirmationToken({ elements, params });
        if (confirmationError) {
            handleError(confirmationError.message);
            return;
        }
        if (data.isOffer) {
            Artcloud.api.post(
                Artcloud.url.vendor.marketplace.invoices.duplicates(data.PublicId),
                { Remote: true },
                (responseData) => {
                    if (location && window.history.replaceState) {
                        window.history.replaceState(null, null, `${location.origin}/invoice/${responseData.PublicId}`);
                    }
                    _handleSubmit(stripe, confirmationToken, responseData.PublicId, handleError);
                },
                (jqXHR) => {
                    handleError(jqXHR && jqXHR.responseJSON && jqXHR.responseJSON.Message);
                }
            );
        } else {
            _handleSubmit(stripe, confirmationToken, data.PublicId, handleError);
        }
    }
    const options = {
        amount: Math.round(data.Balance * 100),
        mode: 'payment',
        currency: data.Gallery && data.Gallery.Currency,
        paymentMethodCreation: 'manual',
        paymentMethodTypes: paymentMethodTypes,
        appearance: {
            theme: 'flat', //stripe, night, flat
            variables: {
                colorPrimary: '#000000',
                colorTextSecondary: '#555555',
                tabIconHoverColor: '#333333'
            },
            rules: {
                '.Input': {
                    color: '#000000'
                },
                '.Label': {
                    color: '#ffffff'
                },
                '.Tab:hover': {
                    color: '#333333'
                },
                '.Tab--selected': {
                    backgroundColor: '#00b1ff'
                }
            }
        }
    }
    if (data.Gallery.IsExpress) {
        options.onBehalfOf = data.Gallery.StripeUserId;
    }
    if (!isAffirm) {
        options.setup_future_usage = 'off_session';
    }
    return (
        <Popup className={`form__container form--popup form--buy popup--${type}-pay popup--cont`} type="popup" onClose={handleClose}>
            <div className="form__artwork" style={{ paddingLeft: 'initial' }}>
                <div style={{ fontSize: '16px', marginBottom: '10px' }}>
                    {
                        !data.isOffer &&
                        <>
                            <div className="art__user">Invoice # {data.Number}</div>
                            <div className="art__title">Grand Total: {totals.GrandTotal}</div>
                        </>
                    }
                    <div className="art__title">Balance: {totals.Balance}</div>
                </div>
            </div>
            {
                stripePromise != undefined &&
                <Elements options={options} stripe={stripePromise}>
                    <StripeElementsCheckoutForm {...props} onClose={handleClose} onReady={resetLoading} onSubmit={handleSubmit} />
                </Elements>
            }
            {
                loading &&
                <LoadingFull style={{ backgroundColor: '#222' }} />
            }
        </Popup>
    )
}

function StripeElementsCheckoutForm(props) {
    const stripe = useStripe();
    const elements = useElements();

    const [error, setError] = React.useState(undefined);
    const [currentPaymentType, setCurrentPaymentType] = React.useState(undefined);
    const handlePaymentElementChange = (e) => {
        if (!e || e.elementType != 'payment' || !e.value || e.value.type == currentPaymentType) {
            return;
        }
        setCurrentPaymentType(e.value.type);
        setError(undefined);
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(undefined);

        if (!stripe || !elements) {
            return;
        }
        props.onSubmit(stripe, elements, (success, message) => {
            if (!success) {
                setError(message || genericErrorMessage);
            } else {
                props.onClose();
            }
        });
    }
    return (
        <div>
            <form onSubmit={handleSubmit}>
                <PaymentElement onChange={handlePaymentElementChange} onReady={props.onReady} />
                <button id="submit" className="btn btn--light btn--fill" style={{ cursor: 'pointer', marginTop: '30px' }}>Submit</button>
                {
                    error != undefined &&
                    <div id="error-message" style={{ color: 'red', fontSize: '14px', marginTop: '20px' }}>
                        {error}
                    </div>
                }
            </form>
        </div>
    )
}

const Invoice = forResource(_Invoice, Artcloud.url.vendor.marketplace.invoices.get);
export default Invoice;